import { useState } from "react";
import { Facebook, Linkedin, Youtube } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import logoWhite from "@/assets/Logo_White.webp";
import hipaaBadge from "@/assets/hipaa-badge.webp";
import { supabase } from "@/integrations/supabase/client";
import { subscribeToMailchimp } from "@/lib/mailchimp";

const socialLinks = [
  { Icon: Facebook, href: "https://www.facebook.com/GopticHub" },
  { Icon: Linkedin, href: "https://www.linkedin.com/company/goptichub/" },
  { Icon: Youtube, href: "https://www.youtube.com/channel/UC_0kCoBcSYk5NjtNHQ8YaEg" },
];

// Newsletter footer signups go to Mailchimp only — never HubSpot.

const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    const submissionId = crypto.randomUUID();
    try {
      // Newsletter: Mailchimp only (no HubSpot per spec). status=subscribed
      // with consent:marketing-pending tag; our Mailchimp journey handles the
      // confirmation + reminder, and grants consent:marketing on confirm.
      subscribeToMailchimp({
        email: trimmed,
        tagsToAdd: ["source:newsletter", "consent:marketing-pending"],
        status: "subscribed",
      });

      // Internal notification — newsletter signup goes to marketing@
      supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "contact-notification",
          recipientEmail: "marketing@goptichub.com",
          idempotencyKey: `newsletter-notify-${submissionId}`,
          templateData: {
            name: "Newsletter signup",
            email: trimmed,
            pageUrl: window.location.href,
            formType: "New newsletter signup",
          },
        },
      }).catch((e) => console.error("Newsletter notification email failed:", e));

      toast.success("You're subscribed! Please check your inbox to confirm.");

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "newsletter_subscribe",
        form_location: "footer",
      });

      setEmail("");
    } catch (err) {
      console.error("Newsletter subscription error:", err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      try {
        await supabase.from("form_submissions").insert({
          form_type: "newsletter",
          email: trimmed,
          page_url: window.location.href,
          page_name: document.title,
          utm_source: "website_footer_newsletter",
          raw_payload: { email: trimmed, source: "site_footer" },
        } as never);
      } catch (e) {
        console.error("Backup insert failed:", e);
      }
      setLoading(false);
    }
  };

  return (
    <footer className="bg-foreground py-12">
      <div className="container mx-auto">
        <div className="grid gap-8 md:grid-cols-6">
          {/* Logo + social + newsletter */}
          <div className="md:col-span-2">
            <img src={logoWhite} alt="GoHub" loading="lazy" width="120" height="32" className="mb-4 h-8 w-auto" />
            <p className="mb-4 text-sm text-primary-foreground/60">
              The all-in-one platform for selling prescription eyewear online.
            </p>
            <div className="mb-6 flex gap-3">
              {socialLinks.map(({ Icon, href }, i) => (
                <a
                  key={i}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-foreground/10 text-primary-foreground/70 transition-colors hover:bg-primary hover:text-primary-foreground"
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
            <div>
              <h4 className="mb-2 text-sm font-bold text-primary-foreground">Stay in the loop</h4>
              <p className="mb-3 text-xs text-primary-foreground/60">
                Product updates, launches, and eyewear ecommerce insights.
              </p>
              <form onSubmit={handleSubscribe} className="flex max-w-sm gap-2">
                <Input
                  type="email"
                  placeholder="Your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-9 border-primary-foreground/20 bg-primary-foreground/10 text-sm text-primary-foreground placeholder:text-primary-foreground/40"
                  aria-label="Email address"
                />
                <Button size="sm" type="submit" disabled={loading} className="shrink-0 rounded-full px-4 text-xs">
                  {loading ? "..." : "Subscribe"}
                </Button>
              </form>
              <p className="mt-2 text-[10px] text-primary-foreground/40">
                We use double opt-in. You can unsubscribe anytime.
              </p>
            </div>
          </div>

          {/* Solutions */}
          <div>
            <h4 className="mb-3 text-sm font-bold text-primary-foreground">Solutions</h4>
            <ul className="space-y-2 text-sm text-primary-foreground/60">
              <li><Link to="/pharmacy" className="hover:text-primary-foreground">Pharmacies & Drugstores</Link></li>
              <li><Link to="/brands-resellers" className="hover:text-primary-foreground">Frame Suppliers</Link></li>
              <li><Link to="/opticians" className="hover:text-primary-foreground">Opticians</Link></li>
              <li><Link to="/dropshipping" className="hover:text-primary-foreground">Dropshipping</Link></li>
              <li><Link to="/virtual-try-on" className="hover:text-primary-foreground">Virtual Try-On</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="mb-3 text-sm font-bold text-primary-foreground">Company</h4>
            <ul className="space-y-2 text-sm text-primary-foreground/60">
              <li><Link to="/about" className="hover:text-primary-foreground">About</Link></li>
              <li><Link to="/blog" className="hover:text-primary-foreground">Blog</Link></li>
              <li><Link to="/contact" className="hover:text-primary-foreground">Contact</Link></li>
              <li>
                <button
                  onClick={() => { (window as Window & { __gohubOpenCookieSettings?: boolean }).__gohubOpenCookieSettings = true; window.dispatchEvent(new Event("open-cookie-settings")); }}
                  className="hover:text-primary-foreground"
                >
                  Cookie Settings
                </button>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="mb-3 text-sm font-bold text-primary-foreground">Legal</h4>
            <ul className="space-y-2 text-sm text-primary-foreground/60">
              <li><Link to="/terms-of-use" className="hover:text-primary-foreground">Terms of Use</Link></li>
              <li><Link to="/privacy-policy" className="hover:text-primary-foreground">Privacy Policy</Link></li>
              <li><Link to="/cookie-policy" className="hover:text-primary-foreground">Cookie Policy</Link></li>
              <li><Link to="/wizard-consumer-terms" className="hover:text-primary-foreground">Optical Wizard Consumer Terms</Link></li>
              <li><Link to="/do-not-sell" className="hover:text-primary-foreground">Do Not Sell or Share My Personal Information</Link></li>
              <li>
                <a
                  href="https://www.wikidata.org/wiki/Q139557088"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-primary-foreground"
                >
                  Wikidata
                </a>
              </li>
            </ul>
          </div>

          {/* HIPAA Badge */}
          <div className="flex items-center justify-center md:justify-end">
            <img src={hipaaBadge} alt="HIPAA Compliancy Group Badge" loading="lazy" width="112" height="112" className="h-28 w-auto" />
          </div>
        </div>

        <div className="mt-8 border-t border-primary-foreground/10 pt-6 text-center text-xs text-primary-foreground/60">
          <p className="mb-2">GoHub is operated by Global Optic Hub Ltd. The B2B prescription eyewear commerce platform.</p>
          <p>© {new Date().getFullYear()} GoHub. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
